// SUB-OFFICE 7 — seeded randomness. The Bureau's chaos is deterministic:
// every player sees the identical building; every "random" pick that must not
// drift derives from a hash. mulberry32 for streams, fnv1a for one-shots.

/** FNV-1a 32-bit string hash (identical to the worker's). */
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 PRNG — returns a () => float in [0,1). Seed with any uint32. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Seeded stream from a string seed. */
export function streamFrom(seed: string): () => number {
  return mulberry32(fnv1a(seed));
}

/** Deterministic jitter in 0.0 .. 0.9 for a given id+axis (worker-identical). */
export function hashJitter(id: string, axis: string): number {
  return (fnv1a(id + ':' + axis) % 10) / 10;
}

/** Seeded pick from an array. */
export function pickSeeded<T>(arr: readonly T[], seed: string): T {
  return arr[fnv1a(seed) % arr.length];
}

/** Seeded integer in [min, max] inclusive. */
export function intSeeded(min: number, max: number, seed: string): number {
  return min + (fnv1a(seed) % (max - min + 1));
}

/**
 * No-repeat LRU picker: never returns any of the last `memory` picks
 * (and therefore never the same line twice in a row). Used for chyron idle
 * lines, closers, movement grammar. Non-seeded on purpose — ambience may drift.
 */
export class LruPick<T> {
  private recent: T[] = [];
  constructor(
    private pool: readonly T[],
    private memory: number
  ) {}

  next(): T {
    if (this.pool.length === 0) throw new Error('LruPick: empty pool');
    const usable = this.pool.filter((p) => !this.recent.includes(p));
    const source = usable.length > 0 ? usable : this.pool;
    const pick = source[Math.floor(Math.random() * source.length)];
    this.recent.push(pick);
    const cap = Math.min(this.memory, this.pool.length - 1);
    while (this.recent.length > Math.max(0, cap)) this.recent.shift();
    return pick;
  }
}
